import { Box, Typography } from "@mui/material";
import {
  buildMinimalShellCarrier,
  buildShellCarrierFromListRow,
  CarrierDetailBookings,
  CarrierDetailCompliance,
  CarrierDetailDriversTable,
  CarrierDetailDriversToolbar,
  CarrierDetailEquipmentTable,
  CarrierDetailEquipmentToolbar,
  CarrierDetailOverview,
  CarrierDetailPageShell,
  CarrierDetailToolbar,
  CARRIER_DETAIL_TAB_QUERY_KEY,
  CARRIER_DETAIL_TAB_VALUES,
  ConfirmActionDialog,
  ListTablePaginationBar,
  buildBlacklistConfirmDescription,
  buildBlacklistSuccessDescription,
  buildUnblacklistConfirmDescription,
  buildUnblacklistSuccessDescription,
  getNextToggleSortOrder,
  getThunkErrorMessage,
  isCarrierYardBlacklisted,
  parseCarrierDetailTabParam,
  useMemoryLeakPrevention,
  useNotificationSnackbar,
  type CarrierDetailTab,
} from "design-components";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { CARRIER_BOOKINGS_SORT_ARRIVAL_UI } from "api-client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CarrierDetailBookingsOverlays } from "../components/carrier-detail-bookings-overlays.component";
import { useCarrierDetailBookingsActions } from "../hooks/use-carrier-detail-bookings-actions";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  CARRIER_BOOKINGS_PAGE_SIZE,
  CARRIER_DRIVERS_PAGE_SIZE,
  CARRIER_EQUIPMENT_PAGE_SIZE,
  fetchCarrierBookings,
  fetchCarrierCompliance,
  fetchCarrierDetail,
  fetchCarrierDrivers,
  fetchCarrierEquipment,
  resetCarrierBookingsList,
  setCarrierYardBlacklist,
  setCarrierBookingsDateRange,
  setCarrierBookingsListRange,
  setCarrierBookingsSearch,
  setCarrierBookingsSort,
  setCarrierBookingsStatusFilters,
  setCarrierDriversListRange,
  setCarrierDriversSearch,
  setCarrierDriversShowOnlyActive,
  setCarrierDriversSort,
  setCarrierEquipmentListRange,
  setCarrierEquipmentSearch,
  setSelectedCarrierId,
} from "../../../shared/store/carrierDetail";
import {
  selectCarrierBookingsError,
  selectCarrierBookingsFromIndex,
  selectCarrierBookingsLoading,
  selectCarrierBookingsRows,
  selectCarrierBookingsSortField,
  selectCarrierBookingsSortOrder,
  selectCarrierBookingsStatusFilterValues,
  selectCarrierBookingsTotal,
  selectCarrierComplianceData,
  selectCarrierComplianceLoading,
  selectCarrierDetail,
  selectCarrierDetailError,
  selectCarrierDetailLoading,
  selectCarrierDetailUsers,
  selectCarrierDriversError,
  selectCarrierDriversFromIndex,
  selectCarrierDriversLoading,
  selectCarrierDriversRows,
  selectCarrierDriversSearch,
  selectCarrierDriversShowOnlyActive,
  selectCarrierDriversSortField,
  selectCarrierDriversSortOrder,
  selectCarrierDriversTotal,
  selectCarrierEquipmentError,
  selectCarrierEquipmentFromIndex,
  selectCarrierEquipmentLoading,
  selectCarrierEquipmentRows,
  selectCarrierEquipmentSearch,
  selectCarrierEquipmentTotal,
} from "../../../shared/store/carrierDetail/carrier-detail.selector";
import { selectCarriersListRows } from "../../../shared/store/carriers/carriers.selector";

export const CarrierDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { showNotification, snackbar } = useNotificationSnackbar();

  const carrier = useAppSelector(selectCarrierDetail);
  const users = useAppSelector(selectCarrierDetailUsers);
  const loading = useAppSelector(selectCarrierDetailLoading);
  const error = useAppSelector(selectCarrierDetailError);
  const carriersListRows = useAppSelector(selectCarriersListRows);

  const driversRows = useAppSelector(selectCarrierDriversRows);
  const driversTotal = useAppSelector(selectCarrierDriversTotal);
  const driversLoading = useAppSelector(selectCarrierDriversLoading);
  const driversError = useAppSelector(selectCarrierDriversError);
  const driversFromIndex = useAppSelector(selectCarrierDriversFromIndex);
  const driversSearch = useAppSelector(selectCarrierDriversSearch);
  const driversShowOnlyActive = useAppSelector(selectCarrierDriversShowOnlyActive);
  const driversSortField = useAppSelector(selectCarrierDriversSortField);
  const driversSortOrder = useAppSelector(selectCarrierDriversSortOrder);
  const complianceData = useAppSelector(selectCarrierComplianceData);
  const complianceLoading = useAppSelector(selectCarrierComplianceLoading);

  const equipmentRows = useAppSelector(selectCarrierEquipmentRows);
  const equipmentTotal = useAppSelector(selectCarrierEquipmentTotal);
  const equipmentLoading = useAppSelector(selectCarrierEquipmentLoading);
  const equipmentError = useAppSelector(selectCarrierEquipmentError);
  const equipmentFromIndex = useAppSelector(selectCarrierEquipmentFromIndex);
  const equipmentSearch = useAppSelector(selectCarrierEquipmentSearch);

  const bookingsRows = useAppSelector(selectCarrierBookingsRows);
  const bookingsTotal = useAppSelector(selectCarrierBookingsTotal);
  const bookingsLoading = useAppSelector(selectCarrierBookingsLoading);
  const bookingsError = useAppSelector(selectCarrierBookingsError);
  const bookingsFromIndex = useAppSelector(selectCarrierBookingsFromIndex);
  const bookingsSortField = useAppSelector(selectCarrierBookingsSortField);
  const bookingsSortOrder = useAppSelector(selectCarrierBookingsSortOrder);
  const bookingsStatusFilters = useAppSelector(selectCarrierBookingsStatusFilterValues);

  const bookingsActions = useCarrierDetailBookingsActions({
    dispatch,
    bookingsRows,
    showNotification,
    pageCarrierId: id ?? null,
  });

  const [yardBlacklistConfirmOpen, setYardBlacklistConfirmOpen] = useState(false);
  const [yardBlacklistSubmitting, setYardBlacklistSubmitting] = useState(false);

  const isYardBlacklisted = useMemo(
    () => isCarrierYardBlacklisted(carrier?.status),
    [carrier?.status],
  );

  const listRow = useMemo(
    () => (id ? carriersListRows.find((row) => row.id === id) ?? null : null),
    [carriersListRows, id],
  );

  const shellCarrier = useMemo(() => {
    if (carrier) return carrier;
    if (listRow) return buildShellCarrierFromListRow(listRow);
    if (id) return buildMinimalShellCarrier(id);
    return null;
  }, [carrier, id, listRow]);

  const headerCarrier = carrier ?? shellCarrier;
  const overviewUsers = carrier ? users : [];
  const overviewLoading = loading || (!carrier && !error);
  const overviewError = error;

  const carrierDisplayName = useMemo(() => {
    if (!headerCarrier) return "";
    return headerCarrier.carrierName?.trim() || headerCarrier.companyLegalName?.trim() || "Carrier";
  }, [headerCarrier]);

  const activeTab = parseCarrierDetailTabParam(searchParams.get(CARRIER_DETAIL_TAB_QUERY_KEY));

  const handleTabChange = useCallback(
    (tab: CarrierDetailTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "overview") {
            next.delete(CARRIER_DETAIL_TAB_QUERY_KEY);
          } else {
            next.set(CARRIER_DETAIL_TAB_QUERY_KEY, tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useMemoryLeakPrevention();

  useEffect(() => {
    if (id) {
      dispatch(setSelectedCarrierId(id));
      dispatch(fetchCarrierDetail({ carrierId: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (activeTab === "drivers" && id) {
      dispatch(fetchCarrierDrivers());
    }
  }, [dispatch, activeTab, id]);

  useEffect(() => {
    if (activeTab === "compliance" && id) {
      dispatch(fetchCarrierCompliance());
    }
  }, [dispatch, activeTab, id]);

  useEffect(() => {
    if (activeTab === "equipment" && id) {
      dispatch(fetchCarrierEquipment({ carrierId: id }));
    }
  }, [dispatch, activeTab, id]);

  const resetBookingsListFirstPage = useCallback(() => {
    dispatch(
      setCarrierBookingsListRange({
        fromIndex: 0,
        toIndex: CARRIER_BOOKINGS_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  const onInitBookingsForCarrier = useCallback(
    (carrierId: string, dateFrom: string | null, dateTo: string | null) => {
      dispatch(resetCarrierBookingsList());
      dispatch(setCarrierBookingsDateRange({ dateFrom, dateTo }));
      dispatch(
        setCarrierBookingsSort({
          sortingField: CARRIER_BOOKINGS_SORT_ARRIVAL_UI,
          sortingOrder: "asc",
        }),
      );
      resetBookingsListFirstPage();
      dispatch(fetchCarrierBookings({ carrierId }));
    },
    [dispatch, resetBookingsListFirstPage],
  );

  const onDisposeBookingsForCarrier = useCallback(() => {
    dispatch(resetCarrierBookingsList());
  }, [dispatch]);

  const onBookingsSearch = useCallback(
    (value: string) => {
      if (!id) return;
      dispatch(setCarrierBookingsSearch(value));
      resetBookingsListFirstPage();
      dispatch(fetchCarrierBookings({ carrierId: id }));
    },
    [dispatch, id, resetBookingsListFirstPage],
  );

  const onBookingsSetStatusFilters = useCallback(
    (values: string[]) => {
      if (!id) return;
      dispatch(setCarrierBookingsStatusFilters(values));
      resetBookingsListFirstPage();
      dispatch(fetchCarrierBookings({ carrierId: id }));
    },
    [dispatch, id, resetBookingsListFirstPage],
  );

  const onBookingsSetDateRange = useCallback(
    (dateFrom: string | null, dateTo: string | null) => {
      if (!id) return;
      dispatch(setCarrierBookingsDateRange({ dateFrom, dateTo }));
      resetBookingsListFirstPage();
      dispatch(fetchCarrierBookings({ carrierId: id }));
    },
    [dispatch, id, resetBookingsListFirstPage],
  );

  const onBookingsSortFieldClick = useCallback(
    (field: string) => {
      if (!id) return;
      const sameField = bookingsSortField === field;
      const nextOrder = getNextToggleSortOrder(sameField, bookingsSortOrder);
      dispatch(
        setCarrierBookingsSort({
          sortingField: field,
          sortingOrder: nextOrder,
        }),
      );
      resetBookingsListFirstPage();
      dispatch(fetchCarrierBookings({ carrierId: id }));
    },
    [bookingsSortField, bookingsSortOrder, dispatch, id, resetBookingsListFirstPage],
  );

  const bookingsListPage = Math.floor(bookingsFromIndex / CARRIER_BOOKINGS_PAGE_SIZE) + 1;

  const handleBookingsPageChange = useCallback(
    (nextPage: number) => {
      if (!id) return;
      const from = (nextPage - 1) * CARRIER_BOOKINGS_PAGE_SIZE;
      dispatch(
        setCarrierBookingsListRange({
          fromIndex: from,
          toIndex: from + CARRIER_BOOKINGS_PAGE_SIZE,
        }),
      );
      dispatch(fetchCarrierBookings({ carrierId: id }));
    },
    [dispatch, id],
  );

  const tabParam = searchParams.get(CARRIER_DETAIL_TAB_QUERY_KEY);
  useEffect(() => {
    if (tabParam && !(CARRIER_DETAIL_TAB_VALUES as readonly string[]).includes(tabParam)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(CARRIER_DETAIL_TAB_QUERY_KEY);
          return next;
        },
        { replace: true },
      );
    }
  }, [tabParam, setSearchParams]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleBlacklistFromYard = useCallback(() => {
    setYardBlacklistConfirmOpen(true);
  }, []);

  const yardBlacklistConfirmDescription = useMemo(() => {
    if (isYardBlacklisted) {
      return buildUnblacklistConfirmDescription([carrierDisplayName], 1);
    }
    return buildBlacklistConfirmDescription([carrierDisplayName], 1);
  }, [carrierDisplayName, isYardBlacklisted]);

  const handleYardBlacklistConfirm = useCallback(async () => {
    if (!carrier?.carrierId) return;

    setYardBlacklistSubmitting(true);
    try {
      await dispatch(
        setCarrierYardBlacklist({
          carrierId: carrier.carrierId,
          action: isYardBlacklisted ? "unblacklist" : "blacklist",
        }),
      ).unwrap();
      setYardBlacklistConfirmOpen(false);
      showNotification({
        variant: "success",
        title: isYardBlacklisted ? "Unblacklisted from yard" : "Carrier blacklisted",
        description: isYardBlacklisted
          ? buildUnblacklistSuccessDescription([carrierDisplayName], 1)
          : buildBlacklistSuccessDescription([carrierDisplayName], 1),
      });
    } catch (e: unknown) {
      showNotification({
        variant: "error",
        title: isYardBlacklisted ? "Unblacklist failed" : "Blacklist failed",
        description: getThunkErrorMessage(
          e,
          isYardBlacklisted ? "Could not unblacklist carrier from yard." : "Could not blacklist carrier.",
        ),
      });
    } finally {
      setYardBlacklistSubmitting(false);
    }
  }, [carrier?.carrierId, carrierDisplayName, dispatch, isYardBlacklisted, showNotification]);

  const resetDriversListFirstPage = useCallback(() => {
    dispatch(
      setCarrierDriversListRange({
        fromIndex: 0,
        toIndex: CARRIER_DRIVERS_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  const handleDriversSearchTrigger = useCallback(
    (value: string) => {
      dispatch(setCarrierDriversSearch(value));
      resetDriversListFirstPage();
      dispatch(fetchCarrierDrivers());
    },
    [dispatch, resetDriversListFirstPage],
  );

  const handleDriversShowOnlyActiveChange = useCallback(
    (value: boolean) => {
      dispatch(setCarrierDriversShowOnlyActive(value));
      resetDriversListFirstPage();
      dispatch(fetchCarrierDrivers());
    },
    [dispatch, resetDriversListFirstPage],
  );

  const driversListPage = Math.floor(driversFromIndex / CARRIER_DRIVERS_PAGE_SIZE) + 1;

  const handleDriversPageChange = useCallback(
    (nextPage: number) => {
      const from = (nextPage - 1) * CARRIER_DRIVERS_PAGE_SIZE;
      dispatch(
        setCarrierDriversListRange({
          fromIndex: from,
          toIndex: from + CARRIER_DRIVERS_PAGE_SIZE,
        }),
      );
      dispatch(fetchCarrierDrivers());
    },
    [dispatch],
  );

  const handleDriversSortFieldClick = useCallback(
    (field: string) => {
      const sameField = driversSortField === field;
      const nextOrder = getNextToggleSortOrder(sameField, driversSortOrder);
      dispatch(
        setCarrierDriversSort({
          sortField: field,
          sortOrder: nextOrder,
        }),
      );
      resetDriversListFirstPage();
      dispatch(fetchCarrierDrivers());
    },
    [dispatch, driversSortField, driversSortOrder, resetDriversListFirstPage],
  );

  const resetEquipmentListFirstPage = useCallback(() => {
    dispatch(
      setCarrierEquipmentListRange({
        fromIndex: 0,
        toIndex: CARRIER_EQUIPMENT_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  const handleEquipmentSearchTrigger = useCallback(
    (value: string) => {
      if (!id) return;
      dispatch(setCarrierEquipmentSearch(value));
      resetEquipmentListFirstPage();
      dispatch(fetchCarrierEquipment({ carrierId: id }));
    },
    [dispatch, id, resetEquipmentListFirstPage],
  );

  const equipmentListPage = Math.floor(equipmentFromIndex / CARRIER_EQUIPMENT_PAGE_SIZE) + 1;

  const handleEquipmentPageChange = useCallback(
    (nextPage: number) => {
      if (!id) return;
      const from = (nextPage - 1) * CARRIER_EQUIPMENT_PAGE_SIZE;
      dispatch(
        setCarrierEquipmentListRange({
          fromIndex: from,
          toIndex: from + CARRIER_EQUIPMENT_PAGE_SIZE,
        }),
      );
      dispatch(fetchCarrierEquipment({ carrierId: id }));
    },
    [dispatch, id],
  );

  if (!id) {
    return null;
  }

  if (error && !shellCarrier) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!shellCarrier) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Carrier not found
        </Typography>
      </Box>
    );
  }

  const displayCarrier = carrier ?? shellCarrier;

  let tabContent: ReactNode = null;
  switch (activeTab) {
    case "overview":
      tabContent = (
        <CarrierDetailOverview
          carrier={displayCarrier}
          users={overviewUsers}
          loading={overviewLoading}
          error={overviewError}
        />
      );
      break;
    case "drivers":
      tabContent = (
        <div className="users-page__table-scroll">
          <CarrierDetailDriversTable
            loading={driversLoading}
            rows={driversError ? [] : driversRows}
            sortField={driversSortField}
            sortDescending={driversSortOrder === "desc"}
            onSortFieldClick={handleDriversSortFieldClick}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={driversError ? 0 : driversTotal}
                page={driversListPage}
                pageSize={CARRIER_DRIVERS_PAGE_SIZE}
                onPageChange={handleDriversPageChange}
                disabled={driversLoading}
              />
            }
          />
        </div>
      );
      break;
    case "bookings":
      tabContent = (
        <CarrierDetailBookings
          carrierId={String(displayCarrier.carrierId)}
          rows={bookingsRows}
          loading={bookingsLoading}
          error={bookingsError}
          totalBookings={bookingsTotal}
          page={bookingsListPage}
          pageSize={CARRIER_BOOKINGS_PAGE_SIZE}
          onPageChange={handleBookingsPageChange}
          sortingField={bookingsSortField}
          sortingOrder={bookingsSortOrder}
          appliedStatusFilterValues={bookingsStatusFilters}
          onInitForCarrier={onInitBookingsForCarrier}
          onDisposeForCarrier={onDisposeBookingsForCarrier}
          onSearch={onBookingsSearch}
          onSetStatusFilters={onBookingsSetStatusFilters}
          onSetDateRange={onBookingsSetDateRange}
          onSortFieldClick={onBookingsSortFieldClick}
          onBookingNumberClick={bookingsActions.handleBookingNumberClick}
        />
      );
      break;
    case "equipment":
      tabContent = (
        <div className="users-page__table-scroll">
          <CarrierDetailEquipmentTable
            loading={equipmentLoading}
            rows={equipmentError ? [] : equipmentRows}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={equipmentError ? 0 : equipmentTotal}
                page={equipmentListPage}
                pageSize={CARRIER_EQUIPMENT_PAGE_SIZE}
                onPageChange={handleEquipmentPageChange}
                disabled={equipmentLoading}
              />
            }
          />
        </div>
      );
      break;
    case "compliance":
      tabContent = (
        <CarrierDetailCompliance
          compliance={complianceData}
          loading={complianceLoading}
        />
      );
      break;
    default:
      tabContent = null;
  }

  return (
    <CarrierDetailPageShell
      carrier={displayCarrier}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onBack={handleBack}
    >
      <>
        <Box className="users-page__main">
          {activeTab === "overview" && carrier ? (
            <CarrierDetailToolbar
              visible
              isBlacklisted={isYardBlacklisted}
              onBlacklistFromYard={handleBlacklistFromYard}
            />
          ) : null}
          {activeTab === "drivers" ? (
            <CarrierDetailDriversToolbar
              searchValue={driversSearch}
              onSearchTrigger={handleDriversSearchTrigger}
              showOnlyActive={driversShowOnlyActive}
              onShowOnlyActiveChange={handleDriversShowOnlyActiveChange}
            />
          ) : null}
          {activeTab === "equipment" ? (
            <CarrierDetailEquipmentToolbar
              searchValue={equipmentSearch}
              onSearchTrigger={handleEquipmentSearchTrigger}
            />
          ) : null}

          {tabContent}
        </Box>

        <ConfirmActionDialog
          open={yardBlacklistConfirmOpen}
          onClose={() => setYardBlacklistConfirmOpen(false)}
          title={isYardBlacklisted ? "Unblacklist from yard?" : "Blacklist carrier?"}
          description={yardBlacklistConfirmDescription}
          primaryLabel={isYardBlacklisted ? "Unblacklist from yard" : "Blacklist"}
          onPrimaryClick={() => void handleYardBlacklistConfirm()}
          primaryDisabled={yardBlacklistSubmitting}
          primaryLoading={yardBlacklistSubmitting}
        />
        <CarrierDetailBookingsOverlays bookings={bookingsActions} />
        {snackbar}
      </>
    </CarrierDetailPageShell>
  );
};
